import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatDate } from "@/lib/format";
import { toast } from "@/lib/toast";
import {
  assignWorkTimeModel,
  listWorkTimeAssignments,
  listWorkTimeModels,
  removeWorkTimeAssignment,
  type WorkTimeModel,
  type WorkTimeModelAssignment,
} from "@/lib/workTimeModels";

interface EmployeeWorkModelAssignmentsEditorProps {
  userLogin: string;
  userDisplay: string;
  refreshKey?: number;
  onChanged?: () => void;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateDe(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function EmployeeWorkModelAssignmentsEditor({
  userLogin,
  userDisplay,
  refreshKey = 0,
  onChanged,
}: EmployeeWorkModelAssignmentsEditorProps) {
  const [models, setModels] = useState<WorkTimeModel[]>([]);
  const [assignments, setAssignments] = useState<WorkTimeModelAssignment[]>([]);
  const [modelId, setModelId] = useState<number | "">("");
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removingFrom, setRemovingFrom] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [modelRows, assignmentRows] = await Promise.all([
        listWorkTimeModels(),
        listWorkTimeAssignments({ user_login: userLogin }),
      ]);
      setModels(modelRows);
      setAssignments(assignmentRows);
      setModelId((prev) => {
        if (prev !== "") return prev;
        const preferred = modelRows.find((m) => m.is_default) ?? modelRows[0];
        return preferred ? preferred.id : "";
      });
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Arbeitszeitmodelle konnten nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [userLogin]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const handleAssign = async () => {
    if (!modelId || !effectiveFrom) {
      toast.error("Modell und Gültig-ab-Datum wählen.");
      return;
    }
    setSaving(true);
    try {
      await assignWorkTimeModel(userLogin, {
        model_id: Number(modelId),
        user_display: userDisplay,
        effective_from: effectiveFrom,
      });
      toast.success("Arbeitszeitmodell zugewiesen.");
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Zuweisung fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (from: string) => {
    if (!window.confirm(`Zuweisung ab ${formatDateDe(from)} entfernen?`)) return;
    setRemovingFrom(from);
    try {
      await removeWorkTimeAssignment(userLogin, from.slice(0, 10));
      toast.success("Zuweisung entfernt.");
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Entfernen fehlgeschlagen.");
    } finally {
      setRemovingFrom(null);
    }
  };

  return (
    <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-label-tertiary">
        Arbeitszeitmodelle
      </h2>
      <p className="mt-1 text-xs text-label-secondary">
        Modellwechsel gelten ab dem gewählten Datum. Vor dem ersten Wechsel gilt das
        Standardmodell.
      </p>

      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="space-y-1">
              <span className="eh-label">Modell</span>
              <select
                className="eh-input w-full"
                value={modelId}
                onChange={(e) => setModelId(e.target.value ? Number(e.target.value) : "")}
              >
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.is_default ? " (Standard)" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="eh-label">Gültig ab</span>
              <input
                type="date"
                className="eh-input w-full"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </label>
            <div className="flex items-end">
              <Button
                variant="primary"
                className="w-full"
                disabled={saving || !modelId || !effectiveFrom}
                onClick={() => void handleAssign()}
              >
                Zuweisen / Wechseln
              </Button>
            </div>
          </div>

          {assignments.length === 0 ? (
            <p className="mt-4 text-sm text-label-secondary">
              Noch keine individuelle Zuweisung – es gilt das Standardmodell.
            </p>
          ) : (
            <ul className="mt-4 divide-y divide-separator rounded-ios border border-separator">
              {assignments.map((row) => (
                <li
                  key={`${row.user_login}-${row.effective_from}`}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                >
                  <div>
                    <div className="font-medium text-label">
                      ab {formatDateDe(row.effective_from)}: {row.model_name}
                    </div>
                    <div className="text-xs text-label-secondary">
                      {row.assigned_at
                        ? `Gesetzt am ${formatDate(row.assigned_at)}`
                        : "—"}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={removingFrom === row.effective_from}
                    onClick={() => void handleRemove(row.effective_from)}
                  >
                    Entfernen
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
